<template>
    <Slab
        ref="refSlab"
        v-if="slabObject"
        :active="active"
        :object="slabObject"
        :colorPalette="colorPalette"
        :preRevealSticker="data.preRevealSticker"
        :innerCardTexture="innerCardFbo.texture"
        :grader="grader"
        @colorUpdate="onSlabColorUpdate"
    />
    <CardInner
        ref="refCardFront"
        v-if="cardFrontObject"
        :imgUrl="data.imageFront"
        :object="cardFrontObject"
        :active="active"
        name="Card Front"
        :layer="LAYER_CARD_INNER"
        :grader="grader"
        :direction="data.direction"
    />
    <CardInner
        ref="refCardBack"
        v-if="cardBackObject"
        :imgUrl="data.imageBack"
        :object="cardBackObject"
        :active="active"
        name="Card Back"
        :flip="true"
        :layer="LAYER_CARD_INNER"
        :grader="grader"
        :direction="data.direction"
    />

    <ChaseReveals ref="refReveals" @revealComplete="onRevealComplete" v-if="isChaseTier" />
    <GrailReveals
        ref="refReveals"
        v-if="isGrailTier"
        @revealComplete="onRevealComplete"
        :category="pack.category?.value || ''"
    />
    <!-- @lightsDownStart="onLightsDownStart"
    @tunnelStart="onTunnelStart" -->

    <!-- <RevealsWrapper
        v-if="isGrailTier"
        ref="refReveals"
        :card="data"
        :reveals="data.reveals"
        @startLightsDown="onGrailStart"
        @revealComplete="onRevealComplete"
    /> -->

    <Vortex ref="refVortex" :colorPalette="colorPalette" />

    <!-- <Cutaway ref="refCutawayWrapper" v-if="data.preRevealCutawayText">
        <CutawayB ref="refCutaway" :text="data.preRevealCutawayText" />
    </Cutaway> -->

    <CardDetails
        ref="refCardDetails"
        v-if="data.detailText && !externalDetails"
        v-bind="data.detailText"
    />
</template>

<script setup>
    import { useWindowMessage } from '#imports';
    import { normalizeGrader } from '@fanatics/resn-api/constants/graders';
    import {
        CardRevealComplete,
        StepCardsReveal,
        Tier0,
        Tier1,
        Tier2,
        Tier3,
    } from '@fanatics/resn-api/instant-rips';
    import { noop } from '@vueuse/core';
    import { gsap } from 'gsap';
    import { Object3D, WebGLRenderTarget } from 'three';
    import {
        computed,
        onBeforeUnmount,
        onMounted,
        provide,
        reactive,
        ref,
        shallowRef,
        watch,
        watchEffect,
    } from 'vue';

    import { map, randomInt } from '@resn/gozer-math';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { usePane, useRafBool, useRenderer, useResize, useThreeObject } from '@resn/gozer-vue';

    import { usePack } from '../Pack/Pack.vue';
    import CardInner from './CardInner.vue';
    import ChaseReveals from './reveals/chase/ChaseReveals.vue';
    import GrailReveals from './reveals/grail/GrailReveals.vue';
    import Slab from './Slab.vue';
    import RevealsWrapper from '@/components/gl/Cards/reveals/RevealsWrapper.vue';
    import Vortex from '@/components/gl/Cards/Vortex.vue';
    import CardDetails from '~/components/gl/async-experience/CardDetails.vue';
    import {
        BASE_WIPE_ANGLE,
        HIGH_TIER_WIPE_ANGLE,
        LIGHTS_DOWN_WIPE_ANGLE,
        TRANSITIONS_REVEAL,
    } from '~/components/gl/Cards/cardRevealConstants';
    import { BLOOM_INTENSITY, useBloom } from '~/composables/useBloom';
    import { useCardMotion } from '~/composables/useCardMotion';
    import {
        getPaletteIdForTier,
        GRAIL_TUNNEL_REVEAL,
        GRAIL_TUNNEL_SET,
        GRAIL_TUNNEL_WIPE,
        LAYER_CARD_INNER,
        LIGHTS_DOWN_DUR,
        LIGHTS_DOWN_REVEAL_START,
    } from '~/core/constants';
    import { useAudio } from '~/providers/AudioProvider';
    import { useColorPalette } from '~/providers/ColorPaletteProvider';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { useFanatics } from '~/providers/instant-rips/FanaticsProvider';

    onMounted(() => {
        window[`card${props.index}Progress`] = inProgress;
        window[`card${props.index}`] = object;
    });

    const props = defineProps({
        index: { default: 0 },
        active: { default: false },
        visible: { default: false },
        rotation: { default: 0 },
        flip: { default: false },
        motionActive: { default: false },
        activePositionDelay: { default: 0 },

        revealed: { default: false },
        revealProgress: { default: 0 },
        revealAngle: { default: 0 },

        zoom: { default: false },
        forceBaseTier: { default: false },
        // colorPalette: { default: () => [] },
        category: { default: null },
        interactive: { default: false },
        zoomEnabled: { default: true },
        canShowDetails: { default: false },
        /** When true, parent renders {@link CardDetails} (repack detail chrome). */
        externalDetails: { default: false },
        fastReveal: { default: false },
        skipReveal: { default: false },

        data: {
            default: {
                id: 'card-1',
                grader: 'sgc',
                imageFront: '/images/sample-cards/8-front@lg.webp',
                imageBack: '/images/sample-cards/8-back@lg.webp',
                title: 'Mike Trout',
                tier: Tier1,
                condition: 'Mint',
                serialNumber: 1,
                serialTotal: 99,
                gradeBadge: '/textures/card/psa-test@lg.webp',
                parallel: 'Long Parallel Text',

                playerName: {
                    first: 'Mike',
                    last: 'Trout',
                },
                playerNumber: 29,

                teamName: {
                    line1: 'Los Angeles',
                    line2: 'Angels',
                },
                teamBadge: '/textures/card/team-badge@lg.webp',
                teamYear: 2021,
                detailText: null,

                reveals: [],
            },
        },

        // for debugging
        color: { default: '#f00' },
    });

    const propsTransition = {
        transitionRevealId: null,
    };

    const { renderer, clock } = useRenderer();
    const fanatics = useFanatics();
    const bloom = useBloom();
    const audio = useAudio();
    const pack = usePack();

    const showDetails = computed(() => props.canShowDetails && !cardMotion.isZooming.value);

    // const showDetails = ref(true);

    const depthVisible = ref(false);
    const visible = computed(() => props.visible && depthVisible.value);
    // const visible = computed(() => props.visible);

    const active = computed(() => props.active);
    const isActive = ref(false);
    const inActivePosition = ref(false);

    const interactive = computed(() => props.interactive);
    const zoomEnabled = computed(() => props.zoomEnabled);
    const render = ref(false);

    const revealed = computed(() => (isBaseTier.value ? inActivePosition.value : props.revealed));
    const revealedComplete = ref(false);
    const revealFinal = ref(false);

    const motionActive = computed(
        () => props.motionActive && !interactive.value && revealedComplete.value
    );
    const loadedAssets = ref(false);
    const grader = computed(() => normalizeGrader(props.data.grader, props.data.title));
    const modelReady = computed(() => loadedAssets.value && grader.value);

    const isBaseTier = computed(
        () => props.forceBaseTier || props.data.tier === Tier1 || props.data.tier === Tier0
    );
    const isChaseTier = computed(() => props.data.tier === Tier2);
    const isGrailTier = computed(() => props.data.tier === Tier3);
    const isHighTier = computed(() => !isBaseTier.value);
    const paletteId = computed(() => getPaletteIdForTier(props.data.tier));
    const baseRevealPr = computed(() => (isBaseTier.value ? 1 : 0));

    const canZoom = computed(() => revealedComplete.value && props.zoom);
    const canFlip = computed(() => revealedComplete.value && props.flip);
    const canRotate = computed(() => revealedComplete.value);

    const hasLightsDown = computed(() =>
        props.data.reveals.some((reveal) => reveal.type === 'lights-down')
    );

    const innerCardFbo = new WebGLRenderTarget();

    const time = ref(0);
    const revealPr = ref(baseRevealPr.value);
    const heartbeatPr = ref(0);
    const depthOffset = ref(0);
    const wipeAngle = ref(BASE_WIPE_ANGLE);
    const lightAngle = ref(0);
    const inProgress = ref(0);
    const position = reactive({ x: 0, y: 0, z: 0 });

    const { sendMessage } = useWindowMessage();
    const { colorPalette, setColorPalette } = useColorPalette();

    const { object } = useThreeObject(null, { name: 'Card' });
    object.visible = false;
    const obj = new Object3D();

    const cardMotion = useCardMotion({
        object,
        motionActive,
        interactive,
        zoomEnabled,
        onHeartbeatRepeat: () => onHeartbeatRepeat(),
    });

    const refSlab = ref(null);
    const refReveals = ref(null);
    const refVortex = ref(null);
    const refCardFront = ref(null);
    const refCardBack = ref(null);
    const refCutawayWrapper = ref(null);
    const refCutaway = ref(null);
    const cardObject = shallowRef(null);
    const slabObject = shallowRef(null);
    const cardFrontObject = shallowRef(null);
    const cardBackObject = shallowRef(null);
    const refCardDetails = ref(null);

    const loader = useGlobalAssets(() => (loadedAssets.value = true));
    const setupModel = () => {
        const cardId = `card-${grader.value}`;
        const gltf = loader.assets[cardId];
        const card = gltf.scene.clone(true);

        card.scale.setScalar(0.4);

        if (cardObject.value) cardObject.value.parent.remove(cardObject.value);

        cardObject.value = card;
        // cardMotion.finalObject.add(card);
        cardMotion.finalObject.add(obj);
        obj.add(card);

        const slab = card.children[1].children[0];
        slabObject.value = slab;

        const front = card.children[0].children[0];
        front.name = 'CardFront';
        cardFrontObject.value = front;

        // createBack
        var back = front.clone(true);
        back.name = 'CardBack';
        front.parent.add(back);
        cardBackObject.value = back;

        revealPr.value = isBaseTier.value ? 1 : 0;

        requestAnimationFrame(() => {
            setActive(active.value);
            setVisible(visible.value);

            setRender();
        });

        emit('loaded');
    };

    onMounted(() => {
        if (modelReady.value) setupModel();
    });

    watch(modelReady, (val) => {
        if (val) setupModel();
    });

    const showPreRevealCutaway = () => {
        gsap.timeline()
            .add(() => refCutawayWrapper.value.show(), 0)
            .add(refCutaway.value.show(), 0)
            .add(() => refCutawayWrapper.value.hide(), '-=1.5');
    };

    let delayRender, delayInActive, delaySweatEffect;

    const setActive = (bool) => {
        // Skip if the state hasn't changed
        if (bool === isActive.value) return;
        isActive.value = bool;

        delayRender?.kill();
        delayInActive?.kill();

        delaySweatEffect?.kill();
        delayHeartbeatEffect?.kill();

        cardMotion.stopHeartbeatEffect();

        if (bool) {
            render.value = true;
            // const tier = revealedCompute.value ? props.data.tier : Tier0;

            const tier = isBaseTier.value ? props.data.tier : Tier0;
            setColorPalette(tier);

            const bloomIntensity = revealed.value || isBaseTier.value ? 0 : BLOOM_INTENSITY;

            gsap.to(bloom.intensity, {
                value: bloomIntensity,
                duration: 1,
                delay: revealed.value ? 0 : 0.2,
                ease: 'power1.inOut',
            });

            delayInActive = gsap.delayedCall(
                props.activePositionDelay,
                () => (inActivePosition.value = true)
            );

            if (revealed.value) {
                // do stuff
            } else {
                if (isHighTier.value) {
                    wipeAngle.value = HIGH_TIER_WIPE_ANGLE;
                    if (!revealed.value) {
                        if (refCutawayWrapper.value) gsap.delayedCall(1, showPreRevealCutaway);
                        const heartbeatDelay = refCutawayWrapper.value ? 4 : 1;
                        delayHeartbeatEffect = gsap.delayedCall(
                            heartbeatDelay,
                            cardMotion.playHeartbeatEffect
                        );
                    }
                } else delaySweatEffect = gsap.delayedCall(6, startSweatEffect);
            }
        } else {
            // Without immediate hiding, a black card placeholder becomes visible
            if (fanatics?.step?.value !== StepCardsReveal) render.value = false;
            else delayRender = gsap.delayedCall(1, () => (render.value = false));

            bloom.reset();
        }
    };

    const setVisible = (val) => {
        object.visible = val;

        onUpdate({ timestamp: 0 });
    };

    const fadeIn = ({ force = false } = {}) => {
        if (force) {
            inProgress.value = 1;
            return;
        }

        gsap.killTweensOf(inProgress);
        gsap.to(inProgress, { value: 1, duration: 0.5, ease: 'power1.inOut' });
    };

    const fadeOut = () => {
        gsap.killTweensOf(inProgress);
        gsap.to(inProgress, { value: 0, duration: 0.5, ease: 'power1.inOut' });
    };

    let tlShow;
    const show = ({
        delay = 0,
        transitionId = propsTransition.transitionRevealId,
        hasTransition = true,
        hasLightAngle = true,
    } = {}) => {
        propsTransition.transitionRevealId = hasTransition
            ? getRevealTransitionId({ id: transitionId })
            : null;

        tlShow?.kill();
        tlShow = gsap
            .timeline({ delay })
            .to(inProgress, { value: 1, duration: 2, ease: 'power1.inOut' }, 0);

        if (hasLightAngle)
            tlShow
                .to(lightAngle, { value: 360, duration: 5 }, 0)
                .set(lightAngle, { value: 0 }, '>')
                .to(lightAngle, { value: 360, duration: 10, ease: 'none', repeat: -1 }, '<');

        revealPr.value = baseRevealPr.value;

        return tlShow;
    };

    const setRevealProgress = (val) => {
        if (revealed.value) return;

        revealPr.value = map(val, 0, 1, 0, 0.8, true);
        bloom.intensity.value = map(val, 0, 0.2, BLOOM_INTENSITY, BLOOM_INTENSITY * 0, true);
    };

    const setRevealAngle = (val) => {
        if (revealed.value) return;

        wipeAngle.value = map(val, 0, 360, 90, 450, true);
    };

    let tlReveal, tlRevealComplete;
    const setRevealed = (bool) => {
        delaySweatEffect?.kill();
        delayHeartbeatEffect?.kill();

        cardMotion.stopHeartbeatEffect();

        tlReveal?.kill();
        tlRevealComplete?.kill();
        tlReveals?.kill();

        if (bool) {
            if (props.skipReveal) {
                revealFinal.value = true;
                revealPr.value = 1;
                return;
            }

            if (active.value) {
                emit('revealStart');

                setColorPalette(props.data.tier);

                const showReveals =
                    refReveals.value &&
                    isHighTier.value &&
                    (props.revealProgress < 0.25 || hasLightsDown.value);

                if (showReveals) playRevealAnimations();
                else onRevealComplete();
            } else revealPr.value = baseRevealPr.value;
        } else {
            if (active.value) setColorPalette(null);

            revealPr.value = baseRevealPr.value;
            revealFinal.value = false;
            revealedComplete.value = false;

            cardMotion.reset();
            refSlab.value?.reset();
        }
    };

    const onRevealComplete = (ignoreTransition = false) => {
        emit('revealCompleteStart');

        const tl = (tlRevealComplete = gsap
            .timeline({
                onComplete: () => {
                    sendMessage(CardRevealComplete, props.index);
                    fanatics.events.emit(CardRevealComplete, props.index);

                    revealedComplete.value = true;
                    propsTransition.transitionRevealId = null;

                    bloom.reset();
                    emit('revealComplete');
                },
            })
            .add(() => (revealFinal.value = true), 0.25));

        wipeAngle.value = BASE_WIPE_ANGLE;

        if (fanatics.fgDimmed.value) {
            fanatics.fgDimmed.value = false;
            revealFinal.value = true;
        }

        const { transitionRevealId } = propsTransition;

        if (ignoreTransition || !transitionRevealId) return;

        const transition = cardMotion.playEffect(transitionRevealId);
        const transitionDuration = transition.revealDuration || 1;
        const delayReveal = 0.2;

        tl.add(() => audio.play('cardReveal'), 0.1);
        tl.to(bloom.intensity, { value: 0, duration: 0.75, ease: 'sine.inOut' }, delayReveal);
        tl.to(revealPr, { value: 1, duration: transitionDuration, ease: 'sine.out' }, delayReveal);

        refSlab.value?.resetTransitionUniforms();
        Object.entries(transition.defines).forEach(([key, value]) => {
            refSlab.value?.setTransitionUniform(key, value);
        });

        if (transition.effects) {
            const { effects } = transition;
            if (effects.includes('vortex')) tl.add(() => refVortex.value?.show(), 0.1);
            if (effects.includes('shine')) {
                if (refCardFront.value && refCardFront.value?.animateShine) {
                    tl.add(
                        () =>
                            refCardFront.value.animateShine(1, transitionDuration, 'power2.inOut'),
                        delayReveal
                    );
                }
            }
        }
    };

    const onHeartbeatRepeat = () => {
        const ease = 'power1.out';

        const delay = 0.05;
        const duration = 0.2;
        const delay2 = delay + duration + 0.1;
        gsap.killTweensOf(heartbeatPr);
        gsap.to(heartbeatPr, { value: 1, duration, delay, ease });
        gsap.to(heartbeatPr, { value: 0, duration: 1.5, delay: delay2, ease });

        audio.play('heartbeat');
    };

    let tlReveals;
    const playRevealAnimations = () => {
        tlReveal = gsap
            .timeline()
            .to(revealPr, { value: 0, duration: 0.5 }, 0)
            .to(bloom.intensity, { value: BLOOM_INTENSITY, duration: 0.5 }, 0);

        if (props.fastReveal) {
            refReveals.value?.show({ fast: true });

            return;
        }

        if (isChaseTier.value) {
            cardMotion.playChaseEffect();
            refReveals.value?.show();
        }

        if (isGrailTier.value) {
            const type = refReveals.value?.show();

            fanatics.fgDimmed.value = type !== 'category';

            let setDelay = GRAIL_TUNNEL_SET;
            let revealDelay;
            let wipeDelay;
            let useWipeReveal = true;

            switch (type) {
                case 'grail-tunnel':
                    revealDelay = GRAIL_TUNNEL_REVEAL;
                    wipeDelay = GRAIL_TUNNEL_WIPE + 0.1;
                    cardMotion.playGrailTunnelEffect();
                    useWipeReveal = false;
                    break;

                case 'lights-down':
                    revealDelay = 4;
                    wipeDelay = revealDelay + 0.5;
                    cardMotion.startLightsDownReveal({ setDelay, revealDelay });

                    tlReveal.set(bloom.intensity, { value: 0 }, setDelay);
                    break;

                case 'category':
                    revealDelay = 3.75;
                    wipeDelay = revealDelay + 0.5;
                    cardMotion.startLightsDownReveal({ setDelay, revealDelay });
                    break;
            }

            tlReveal
                .add(() => (wipeAngle.value = LIGHTS_DOWN_WIPE_ANGLE), setDelay)
                .add(refSlab.value.playGrailAnimation({ revealDelay }), 0)
                .to(bloom.intensity, { value: 0, duration: 1.5, ease: 'sine.inOut' }, wipeDelay);

            if (useWipeReveal) {
                tlReveal.to(revealPr, { value: 1, duration: 5, ease: 'power2.out' }, wipeDelay);
            }

            tlReveal.add(noop, LIGHTS_DOWN_DUR);
        }
    };

    const setShowDetails = (val) => {
        refCardDetails.value?.setActive(val, 0.5);
    };

    const hide = () => {
        tlShow?.kill();
        tlReveals?.kill();
        cardMotion.hide();
    };

    const setRender = () => {};

    const onUpdate = ({ timestamp }) => {
        cardMotion.update(timestamp);

        time.value = clock.getElapsed();

        depthOffset.value = map(object.position.z, -1, 0, 1, 0, true);

        refSlab.value?.onUpdate();
        depthVisible.value = depthOffset.value < 0.98;
    };

    const startSweatEffect = () => {
        delaySweatEffect?.kill();
        if (revealed.value) return;
        cardMotion.playSweatEffect();
        delaySweatEffect = gsap.delayedCall(10, startSweatEffect);
    };

    let delayHeartbeatEffect = null;

    const setRotation = (val) => {
        cardMotion.setRotation(val);

        if (props.motionActive) audio.play('cardRotate');
    };
    const setFlip = (val) => {
        cardMotion.setFlip(val);

        if (props.motionActive) audio.play('cardFlip');
    };
    const setZoom = (val) => {
        cardMotion.setZoom(val);
        // if (props.motionActive) audio.play('cardZoom');
    };

    const setInProgress = (val) => {
        inProgress.value = val;
    };

    const resetInteraction = () => {
        cardMotion.resetInteractionMotion(true);
    };

    const reset = () => {
        setRotation(0);
        setFlip(false);
        setZoom(canZoom.value);

        revealPr.value = baseRevealPr.value;
        inProgress.value = 0;
        lightAngle.value = 0;
        revealedComplete.value = false;
        inActivePosition.value = false;

        cardMotion.reset();

        propsTransition.transitionRevealId = null;
    };

    const getRevealTransitionId = ({ id = null } = {}) => {
        const baseTransitions = [
            TRANSITIONS_REVEAL.SPIN,
            TRANSITIONS_REVEAL.TILT,
            TRANSITIONS_REVEAL.WIGGLE,
        ];
        const chaseTierTransitions = [
            TRANSITIONS_REVEAL.ZOOM,
            TRANSITIONS_REVEAL.BURN,
            TRANSITIONS_REVEAL.TILT,
            TRANSITIONS_REVEAL.WIGGLE,
        ];

        return (
            id ||
            (isGrailTier.value
                ? TRANSITIONS_REVEAL.SPIN
                : isChaseTier.value
                  ? chaseTierTransitions[randomInt(0, chaseTierTransitions.length - 1)]
                  : baseTransitions[randomInt(0, baseTransitions.length - 1)])
        );
    };

    useRafBool(render, onUpdate);

    useResize(({ width, height }) => {
        const dpr = renderer.getPixelRatio();
        innerCardFbo.setSize(width * dpr, height * dpr);
    }, true);

    onBeforeUnmount(() => {
        disposeObject(object);
    });

    const slabColor1 = ref('#87b2ff');
    const slabColor2 = ref('#bd8ef6');
    const onSlabColorUpdate = (color1, color2) => {
        slabColor1.value = color1;
        slabColor2.value = color2;
    };

    provide('card', {
        inProgress,
        revealPr,
        heartbeatPr,
        depthOffset,
        wipeAngle,
        lightAngle,
        time,
        slabColor1,
        slabColor2,
    });

    watch(render, setRender);
    watch(visible, setVisible);
    watch(active, setActive);
    watch(revealed, setRevealed);
    watch(showDetails, (val) => {
        if (props.externalDetails) return;
        setShowDetails(val);
    });

    watch(() => props.revealProgress, setRevealProgress);
    watch(() => props.revealAngle, setRevealAngle);
    watch(() => (canRotate.value ? props.rotation : 0), setRotation);
    watch(canFlip, setFlip);
    watch(canZoom, setZoom);

    const options = { min: 0, max: 1, step: 0.01 };

    const pos = ref({ x: 0, y: 0, z: 0 });
    const rot = ref({ x: 0, y: 0, z: 0 });
    watchEffect(() => obj.position.set(pos.value.x, pos.value.y, pos.value.z));
    watchEffect(() => obj.rotation.set(rot.value.x, rot.value.y, rot.value.z));

    const emit = defineEmits([
        'revealComplete',
        'revealCompleteStart',
        'revealStart',
        'zooming',
        'loaded',
    ]);

    watch(cardMotion.isZooming, (val) => emit('zooming', val));

    const pane = usePane(
        [
            { name: 'pos', value: pos },
            { name: 'rot', value: rot },
            { name: 'revealPr', value: revealPr, options },
            { name: 'inProgress', value: inProgress, options },
            { name: 'wipeAngle', value: wipeAngle, options: { min: 0, max: 360, step: 0.1 } },
            { name: 'lightAngle', value: lightAngle, options: { min: 0, max: 360, step: 0.1 } },
            { name: 'position', value: position },
        ],
        { title: 'Card', subfolder: true, expanded: false }
    );
    pane?.addBlade({
        view: 'list',
        label: 'Post Reveal Transition',
        options: Object.keys(TRANSITIONS_REVEAL).map((key) => ({
            text: key,
            value: TRANSITIONS_REVEAL[key],
        })),
        value: propsTransition.transitionRevealId,
    }).on('change', (e) => (propsTransition.transitionRevealId = e.value));

    defineExpose({
        show,
        hide,
        fadeIn,
        fadeOut,
        reset,
        object,
        setInProgress,
        resetInteraction,
        isZooming: cardMotion.isZooming,
        zoomPr: cardMotion.zoomPr,
        paletteId,
    });
</script>

<style lang="scss" scoped>
    @use '@/styles/shared.scss' as *;

    .cardDetails {
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0%);
        top: 76%;

        @include desktop {
            // top: 67%;
            top: 69%;
        }
    }
</style>
